import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { NEUTRAL } from "../../../../theme/tokens";
import { SheetActionRow } from "../../../UIToolkit/Clickable/SheetActionRow";
import type { GapsShiftGroup } from "../gapsDayModel";
import { staffingDayKey } from "../gapsRequests";
import { GapsSheetKind, openGapsSheet } from "../useGapsSheet";

type Props = {
	/** The group the post still describes; `undefined` asks which shift first. */
	group: GapsShiftGroup | undefined;
	date: DateTime;
	/** The shift is not short, so adding someone is the main way forward and the row comes before the form. */
	isLeading: boolean;
	disabled: boolean;
	testIdPrefix: string;
};

/**
 * The post sheet's "Add staff directly" (APP-886): someone the head nurse has already spoken to goes straight onto the
 * shift — no post, no approval — through the add sheet, which replaces this one. On the shift the post describes, the
 * add starts on its people; without one, it asks which shift first. The post form's own "Post it instead" twin.
 */
export const GapsPostAddDirectlyRow: FC<Props> = ({ group, date, isLeading, disabled, testIdPrefix }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();

	const handleClick = (): void => {
		track("gaps_post_shift_add_directly_clicked", { department_id: chosenDepartment.department_id, group: group?.key ?? null, leading: isLeading });
		openGapsSheet({ kind: GapsSheetKind.AddStaff, dayISO: staffingDayKey(date), scope: null, linkedRequestId: null, groupKey: group?.key ?? null });
	};

	return (
		<Box sx={isLeading ? { mx: 2, mt: 2, border: `1px solid ${NEUTRAL[150]}`, borderRadius: 1 } : { mt: 1, borderTop: `1px solid ${NEUTRAL[150]}` }}>
			<SheetActionRow
				icon={<PersonAddAltOutlinedIcon />}
				label={t("gaps.postSheet.addDirectly")}
				description={
					isLeading && group !== undefined
						? t("gaps.postSheet.addDirectlyAtPar", { job: group.jobLabel, time: group.timeLabel })
						: t("gaps.postSheet.addDirectlyHint")
				}
				showChevron
				disabled={disabled}
				onClick={handleClick}
				dataTestId={`${testIdPrefix}-add-directly`}
			/>
		</Box>
	);
};
